import { GraphQLClientProvider } from 'lib-integrations-partner-widgets';
import { config } from 'lib-shared-backend-config';
import { Sentry, sentrySetFatalScope } from 'lib-shared-sentry';
import { Route, Routes } from 'react-router-dom';

import { ElevioClient } from 'utils/elevio';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useFavoriteAgentsMigration } from 'utils/useFavoriteAgents/useFavoriteAgentsMigration';

import { AppFallback } from './components/appFallback/appFallback';
import { AppModals } from './components/appModals/appModals';
import { AppStatus } from './components/appStatus/appStatus';
import { ColorModeProvider } from './utils/colorMode';
import { useAppInit } from './utils/useAppInit/useAppInit';
import { useAppNetworkChange } from './utils/useAppNetworkChange/useAppNetworkChange';
import { useJabraInit } from './utils/useJabraInit/useJabraInit';
import { LayoutRoutes } from './views/layout';
import { TvDashboardRoutes } from './views/tvDashboard';

/** A component that governs the main entry point of the application. */
export const App = (): JSX.Element => {
  const agent = useAppSelector((state) => state.settings.profile.profile);
  const isAppRunning = useAppSelector(
    ({ app: { asyncStatus, error } }) =>
      asyncStatus.app === 'fulfilled' && !error,
  );

  useAppInit();
  useAppNetworkChange();
  useJabraInit();

  return (
    <Sentry.ErrorBoundary
      beforeCapture={sentrySetFatalScope}
      showDialog
      dialogOptions={{
        user: {
          name: agent.name ?? undefined,
          email: agent.email ?? undefined,
        },
      }}
      fallback={<AppFallback />}
    >
      {!isAppRunning && <AppStatus />}

      {isAppRunning && (
        <ColorModeProvider>
          <GraphQLClientProvider
            configuration={{ baseUrl: config.url.partnerIntegrations }}
          >
            <FavoriteAgentsMigration />

            <AppModals />
            <ElevioClient />

            <Routes>
              <Route path="tv/dashboard/*" element={<TvDashboardRoutes />} />
              <Route path="/*" element={<LayoutRoutes />} />
            </Routes>
          </GraphQLClientProvider>
        </ColorModeProvider>
      )}
    </Sentry.ErrorBoundary>
  );
};

// TODO: CORE-1405 — Remove this migration after rollout is complete.
const FavoriteAgentsMigration = (): null => {
  useFavoriteAgentsMigration();
  return null;
};
